import {
    FHIRAddressSerializer,
    InsuranceCompanyPlanSerializer,
    InsuranceCompanySerializer,
    InsuranceCoverageDetailSerializer,
    InsuranceCoverageTypeSerializer,
    InsuranceSettingsDetailsSerializer,
    InsuranceSettingsStatusSerializer,
    RelatedPersonSerializer,
} from '../@core/api.service';
import {AdditionalName} from './additional-name';
import {InsuranceEligibility} from './insurance-eligibility';
import {
    InsurancePriorityHelper,
} from '../portal/@slide-panel-preview/patient-details/patient-insurance/patient-insurance-edit_LEGACY/insurance-priority-helper';
import {INSURANCE_PLAN_COVERAGE_STATUS_CHOICES} from '../@core/insurance_plan_coverage_status_choices';
import {INSURANCE_SETTINGS_STATUSES} from '../@core/insurance_settings_statuses';
import {SimpleDatePipe} from '../@theme/pipes/simple-date.pipe';
import {Human} from './human';
import {BillingGroupPlan} from './billing-group-plan';
import {assignOrCreateOrNew} from './model-base';

export type InsuranceSettingsStatus = keyof typeof INSURANCE_SETTINGS_STATUSES;

export interface InsuranceSettingsStatusDefinition {
    key: InsuranceSettingsStatus;
    label: string;
    colorClass: string;
    icon: string;
}

export const INSURANCE_SETTINGS_STATUS_DEFINITIONS: {[key in InsuranceSettingsStatus]: InsuranceSettingsStatusDefinition} = {
    SERVICEABLE: {
        key: 'SERVICEABLE',
        label: INSURANCE_SETTINGS_STATUSES.SERVICEABLE,
        colorClass: 'success',
        icon: 'check',
    },
    UNSERVICEABLE: {
        key: 'UNSERVICEABLE',
        label: INSURANCE_SETTINGS_STATUSES.UNSERVICEABLE,
        colorClass: 'danger',
        icon: 'ban',
    },
    PENDING_INVESTIGATION: {
        key: 'PENDING_INVESTIGATION',
        label: INSURANCE_SETTINGS_STATUSES.PENDING_INVESTIGATION,
        colorClass: 'warning',
        icon: 'hourglass-half',
    },
};

export class InsurancePlan implements InsuranceCoverageDetailSerializer {
    id?: number;
    additional_name?: AdditionalName;
    effective_end_date?: Date | string;
    effective_start_date?: Date | string;
    group?: string;
    coverage_types?: Array<InsuranceCoverageTypeSerializer>;
    insurance_setting?: number;
    subscriber?: RelatedPersonSerializer;
    subscriber_identifier?: string;
    insurance_company?: InsuranceCompanySerializer;
    priority?: string;
    type?: number;
    coverage_status?: keyof typeof INSURANCE_PLAN_COVERAGE_STATUS_CHOICES;
    last_eligibility_request?: InsuranceEligibility;
    payer_name?: string;
    address?: FHIRAddressSerializer;
    insurance_company_plan: InsuranceCompanyPlanSerializer;
    billing_group_plan: BillingGroupPlan;

    constructor(x: InsuranceCoverageDetailSerializer) {
        Object.assign(this, x);
        this.billing_group_plan = assignOrCreateOrNew(BillingGroupPlan, x.billing_group_plan);
        this.additional_name = x.additional_name ? new AdditionalName(x.additional_name) : undefined;
        this.last_eligibility_request = new InsuranceEligibility(x.last_eligibility_request);
        if (!this.coverage_types?.length) {
            this.coverage_types = [{coverage_type: 'PROFESSIONAL_CLAIMS'}];
        }
    }

    get hasPriority(): boolean {
        return this.coverage_types?.some(ct => ct.priority) ?? false;
    }

    get isIncomplete() {
        return !this.hasPriority || !this.insurance_company;
    }

    get coverageStatusClass(): string | null {
        if (this.isIncomplete) return 'warning-coverage';

        const verificationStatus = this.last_eligibility_request?.response_status;

        if (!verificationStatus || verificationStatus === 'NOT_SUPPORTED') return null;

        if (verificationStatus === 'ACTIVE_COVERAGE') {
            return 'active-coverage';
        }

        return 'warning-coverage';
    }

    get hasWarning() {
        return this.coverageStatusClass === 'warning-coverage';
    }

    get isPrimary() {
        return this.coverage_types?.some(x => x.priority === 'PRIMARY');
    }
}

export class Insurance implements InsuranceSettingsDetailsSerializer {
    id: number;
    patient: number;
    direct_bill: boolean;
    medicare_number: string;
    medicaid_status: string;
    insurance_coverages: InsurancePlan[];
    medicaid_policy: string;
    status?: InsuranceSettingsStatusSerializer;

    private _empty: boolean;

    get empty() {
        return this._empty;
    }

    constructor(x: InsuranceSettingsDetailsSerializer) {
        const {insurance_coverages, ...rest} = x;
        Object.assign(this, rest);
        const insurancePlans = insurance_coverages.map(p => new InsurancePlan(p));
        this.insurance_coverages = InsurancePriorityHelper.sortInsurancePlans(insurancePlans);
        this._empty = Object.entries(rest).every(([key, val]) => !val || ['id', 'patient', '_empty'].includes(key));
    }

    get statusDef() {
        return Insurance.getStatusDef(this.status?.status);
    }

    get statusTooltip() {
        return Insurance.getStatusTooltip(this.status);
    }

    static getStatusDef(status: string) {
        return status ? INSURANCE_SETTINGS_STATUS_DEFINITIONS[status as InsuranceSettingsStatus] : null;
    }

    static getStatusTooltip(status: InsuranceSettingsStatusSerializer) {
        if (!status) return null;
        let x = `${SimpleDatePipe.transform(status.timestamp, true, true)}`;
        if (status.comment) x += ` - ${status.comment}`;
        return x;
    }

    static getFullStatusTooltip(status: InsuranceSettingsStatusSerializer) {
        if (!status) return null;
        const statusDef = this.getStatusDef(status.status);
        return `${statusDef?.label || status.status} - ${Human.getName(status.user)} @ ${this.getStatusTooltip(status)}`;
    }
}
